import { useState, useEffect } from "react";
import {
  Calendar, Clock, MapPin, Users, Video, Loader2, CheckCircle2,
  UserPlus, ExternalLink
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api from "../../lib/api";

const BookSessionDialog = ({ open, onOpenChange, courseId, courseTitle }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    if (open && courseId) {
      setLoading(true);
      api.get(`/sessions/by-course/${courseId}`)
        .then(r => setSessions(r.data || []))
        .catch(() => setSessions([]))
        .finally(() => setLoading(false));
    }
  }, [open, courseId]);

  const handleBook = async (sessionId) => {
    setBookingId(sessionId);
    try {
      await api.post(`/sessions/${sessionId}/register`);
      toast.success("Session booked successfully!");
      const r = await api.get(`/sessions/by-course/${courseId}`);
      setSessions(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to book session");
    } finally {
      setBookingId(null);
    }
  };

  const handleCancel = async (sessionId) => {
    if (!window.confirm("Cancel your registration?")) return;
    setBookingId(sessionId);
    try {
      await api.delete(`/sessions/${sessionId}/register`);
      toast.success("Registration cancelled");
      const r = await api.get(`/sessions/by-course/${courseId}`);
      setSessions(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setBookingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Book ILT Session</DialogTitle>
          <DialogDescription className="text-xs">{courseTitle}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1" data-testid="book-session-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-10">
              <Calendar className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-sm text-muted-foreground">No upcoming sessions available for this course</p>
              <p className="text-xs text-muted-foreground mt-1">Contact your administrator to schedule one</p>
            </div>
          ) : (
            sessions.map(s => {
              const fillPct = s.capacity > 0 ? Math.round((s.enrolled_count / s.capacity) * 100) : 0;
              return (
                <div
                  key={s.id}
                  className={cn(
                    "p-4 rounded-lg border transition-all",
                    s.is_registered ? "border-green-200 bg-green-50/30" : s.is_full ? "border-red-100 bg-red-50/10 opacity-60" : "hover:border-primary/30"
                  )}
                  data-testid={`session-option-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold">{s.title}</h4>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(s.session_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {s.start_time} - {s.end_time}
                        </span>
                        <span className="flex items-center gap-1">
                          {s.location_type === "virtual" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                          {s.location || "TBD"}
                        </span>
                      </div>
                      {s.instructor_name && (
                        <p className="text-xs text-muted-foreground mt-1">Instructor: {s.instructor_name}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] text-muted-foreground"><Users className="w-3 h-3 inline mr-0.5" />{s.enrolled_count}/{s.capacity}</span>
                        <Progress value={fillPct} className={`h-1 w-16 ${fillPct > 85 ? "[&>div]:bg-red-500" : ""}`} />
                        {s.is_full && <Badge variant="outline" className="text-[9px] text-red-500 border-red-200">Full</Badge>}
                      </div>
                    </div>
                    <div className="shrink-0 mt-1">
                      {s.is_registered ? (
                        <div className="flex flex-col items-end gap-1.5">
                          <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Booked
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[10px] h-6 text-red-500 hover:text-red-600 px-2"
                            onClick={() => handleCancel(s.id)}
                            disabled={bookingId === s.id}
                            data-testid={`cancel-booking-${s.id}`}
                          >
                            {bookingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Cancel"}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={() => handleBook(s.id)}
                          disabled={s.is_full || bookingId === s.id}
                          data-testid={`book-session-${s.id}`}
                        >
                          {bookingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                          {s.is_full ? "Full" : "Book"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookSessionDialog;
